import React, { useRef, useEffect, useState, useContext } from "react";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import MapView from "@arcgis/core/views/MapView";
import SceneView from "@arcgis/core/views/SceneView";
import GroupLayer from "@arcgis/core/layers/GroupLayer";
import { fillStyleTypes,lineStyleTypes,clearDrawing,hexToRgbA, fontfamilies, pointStyleTypes} from "./DrawToolVM";
import Color from "@arcgis/core/Color";
import SnappingControlsWidget from "../snapping-controls/SnappingControlsWidget";
import { FormControl, IconButton, Input, InputLabel, MenuItem, Select, Slider, Stack, TextField, Typography } from "@mui/material";
import GeometryButton from "../../../components/geometry-buttons/GeometryButton";
import {AddGraphicsFromURL} from "../assessor-add-graphic-from-url/AddGraphicFromUrlVM";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import DeleteIcon from "@mui/icons-material/Delete";

interface DrawToolWidgetProps  {
  view: MapView|SceneView,
  activeWidget:string,
  pageConfig?:any
};

const DrawToolWidget = ({ view, activeWidget,pageConfig }:DrawToolWidgetProps) => {
  const [sketchViewModel, setSketchViewModel] = useState<SketchViewModel|undefined>(undefined)
  const[graphicLayer,setGraphicLayer]=useState<null|GraphicsLayer>(null)
  const[activeGeometry,setActiveGeometry]= useState("")
  const [fontfamily, setfontfamily] = useState("Arial");
  const [fillStyle, setFillStyle] = useState("none");
  const [fillColor, setFillColor] = useState("#4ceb34");
  const [fillOpacity, setFillOpacity] = useState("1");
  const [lineStyle, setLineStyle] = useState("solid");
  const [pointStyle, setPointStyle] = useState(Object.keys(pointStyleTypes)[0]);
  const [outlineColor, setOutlineColor] = useState("#fbff05");
  const [outlineOpacity, setOutlineOpacity] = useState("1");
  const [outlineSymboleSize, setOutlineSymbolSize] = useState("3");
  const [fontSize, setFontSize] = useState<string>('16');
  const [haloSize, setHaloSize] = useState("1.5");
  const [symbolSize, setSymbolSize] = useState("12");
  const [symbolAngle,setSymbolAngle]=useState(0);
  const [textLabel,setTextLabel]=useState("Text")
  const [visible, setVisible] = useState(false);
  const drawtoolWidgetDiv = useRef(null);

  const point_SymbolRef=useRef<SimpleMarkerSymbol>(new SimpleMarkerSymbol({
    style: pointStyle as any,
    size: symbolSize,
    angle:symbolAngle,
    color: "#4ceb34",
    outline: {
      color: outlineColor,
      width: outlineSymboleSize,//
    },
  }))
  let point_Symbol=point_SymbolRef.current

  const textKeySymbolRef=useRef<TextSymbol>(new TextSymbol({
    color: fillColor,
    angle:symbolAngle,
    haloColor: outlineColor,
    haloSize: haloSize + "px", // esri-icon-key
    text: textLabel,
    font: {
      // autocasts as new Font()
      size: fontSize,
      family: "sans-serif",
      weight: "bold",
    },
  }))
  let textKeySymbol=textKeySymbolRef.current

  const polygon_SymbolRef=useRef<SimpleFillSymbol>(new SimpleFillSymbol({
    style: fillStyle as any,
    color: fillColor,
    outline: {
      // autocasts as new SimpleLineSymbol()
      color: outlineColor,
      width: 3,
      style: lineStyle as any,
    },
  }))
  let polygon_Symbol=polygon_SymbolRef.current

  const polyline_SymbolRef=useRef<SimpleLineSymbol>(new SimpleLineSymbol({
    style: lineStyle as any,
    color: outlineColor,
    width: 6,
  }))
  let polyline_Symbol=polyline_SymbolRef.current

  useEffect(() => {
    if (activeWidget === "DrawToolWidget") {
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [activeWidget]);

  useEffect(() => {
    if (drawtoolWidgetDiv.current) {
    let drawgraphicsLayer:GraphicsLayer = new GraphicsLayer({
      id: "draw_graphiclyr",title:"Draw Layer"
    });
    setGraphicLayer(drawgraphicsLayer)
    

      let layersArray= view.map.layers.filter(layer=>layer.id==="operationalLayers")

      if(layersArray.length>0){
        let opGroupLayer = layersArray.getItemAt(0) as GroupLayer
        opGroupLayer.add(drawgraphicsLayer,0);
      }
      
      let sketchVM = new SketchViewModel({
        view: view,
        layer: drawgraphicsLayer,
        defaultCreateOptions:{mode:"hybrid"},
        pointSymbol: point_Symbol,
        polylineSymbol: polyline_Symbol,
        polygonSymbol: polygon_Symbol,
        updateOnGraphicClick: true,
        defaultUpdateOptions: { toggleToolOnClick: false, tool: "transform" },

        //    creationMode: "update"
      });
    setSketchViewModel(sketchVM)

    }
  }, []);

  useEffect(()=>{
    if(sketchViewModel){
      let newFillColor=new Color(hexToRgbA(fillColor, fillOpacity));
      let newOutlineColor=new Color(hexToRgbA(outlineColor, outlineOpacity));
      let polyLineSymbol:SimpleLineSymbol = sketchViewModel.polylineSymbol as SimpleLineSymbol
      let polygonSymbol = sketchViewModel.polygonSymbol as SimpleFillSymbol
      let pointSymbol = sketchViewModel.pointSymbol as SimpleMarkerSymbol | TextSymbol
      polyLineSymbol.style=lineStyle as any
      polyLineSymbol.color=newOutlineColor
      polygonSymbol.outline.style=lineStyle as any
      polygonSymbol.outline.color=newOutlineColor
      polygonSymbol.style=fillStyle as any
      polygonSymbol.color=newFillColor
      if(pointSymbol instanceof TextSymbol){
        pointSymbol.haloColor=newOutlineColor
        pointSymbol.angle=symbolAngle
        pointSymbol.text=textLabel
        pointSymbol.font.size=Number(fontSize)
        pointSymbol.font.family = fontfamily
      }
      if(pointSymbol instanceof SimpleMarkerSymbol){
        pointSymbol.size=symbolSize as any
        pointSymbol.angle=symbolAngle as any
        pointSymbol.outline.color=newOutlineColor
        pointSymbol.style= pointStyle as any
      }
      pointSymbol.color=newFillColor
    }

  },[sketchViewModel,activeGeometry,fillStyle,lineStyle,fillOpacity,symbolSize,symbolAngle,fontSize,haloSize,outlineSymboleSize,outlineOpacity,fillColor,outlineColor,textLabel, pointStyle, fontfamily])

  const handlePointStyleChange = (event) => {
    const {value} = event.target;
    setPointStyle(value);
  };

  const handleLineStyleChange = (event) => {
    const {value} = event.target;
    setLineStyle(value);
  };

  const handleFontFamilyChange = (event) => {
    const {value} = event.target;
    setfontfamily(value)
  };

  const handleOutlineColorChange=(event)=>{
    const{value}=event.target;
    setOutlineColor(value)
  }

  const handleOutlineOpacityChange=(event)=>{
    const{value}=event.target;
    setOutlineOpacity(value)
  }

  const handleFillStyleChange=(event)=>{
    const {value} = event.target;
    setFillStyle(value);
  }

  const handleFillOpacityChange=(event)=>{
    const{value}=event.target
    setFillOpacity(value)
  }
  const handleFillColorChange=(event)=>{
    const{value}=event.target
    setFillColor(value)
  }

  const handleSymbolSize=(event)=>{
    const {value}=event.target
    setSymbolSize(value)
  }

  const handleSymbolAngle=(event)=>{
    const {value}=event.target
    setSymbolAngle(value)
  }

  const handleTextLabel=(event)=>{
    const {value}=event.target
    setTextLabel(value)
    
  }

  const handleFontSize=(event)=>{
    const {value}=event.target
    setFontSize(value)
  }

  const handleGeometryChange=(event)=>{
    const {value}=event.target
    if(value==="text"){
      sketchViewModel.pointSymbol=textKeySymbol
      sketchViewModel.create("point")
    }else if(value==='point'){
      sketchViewModel.pointSymbol=point_Symbol
      sketchViewModel.create(value);
    }else{
      sketchViewModel.create(value);
    }
    setActiveGeometry(value)
    
  }

  const pointStyleOptions = Object.keys(pointStyleTypes).map((key) => {
    const {img } = pointStyleTypes[key];
    if(img.length>0){
    return(
      <MenuItem key={`${key}-option`} value={key}>{img && <img src={`${process.env.PUBLIC_URL}/draw-tool-symbols/${img}`} alt={key} />}</MenuItem>
    )

  }
  return(

      <MenuItem key={`${key}-option`} value={key}>{key}</MenuItem>
        
        )

  });

  const lineStyleOptions = Object.keys(lineStyleTypes).map((key) => {
    const {img } = lineStyleTypes[key];
    if(img.length>0){
    return (

        <MenuItem key={`${key}-option`} value={key}>
          {img && <img src={`${process.env.PUBLIC_URL}/draw-tool-symbols/${img}`} alt={key} />} {/* Render the image if available */}
        </MenuItem>
      )

  }
  return(
      <MenuItem key={`${key}-option`} value={key}>
        {key}
      </MenuItem>
    )
  
  });

  const fontFamilyOptions = fontfamilies.map((key) =>{
    return(
        <MenuItem key={`${key}-font-option`} value={key} style={{fontFamily:`${key}`}} >
          {key}
        </MenuItem>
      )
    
  })
  const fillStyleOptions = Object.keys(fillStyleTypes).map((key) => {
    const {img } = fillStyleTypes[key];
    if(img.length>0){
    return (
        <MenuItem key={`${key}-draw-option`} value={key}>
          {img && <img src={`${process.env.PUBLIC_URL}/draw-tool-symbols/${img}`} alt={key} />} {/* Render the image if available */}
        </MenuItem>

    );
  }
  return(
      <MenuItem key={`${key}-draw-option`} value={key}>
        {key}
      </MenuItem>
    )
  
  });


  const GeometryButtons= [["point","map-pin"],["polyline","polyline"],["polygon","polygon"],["circle","circle"],["rectangle","rectangle"],['text',"labels"]].map((item)=>{
    return(
      <GeometryButton
      key={`draw-tool-${item[1]}-geometry-button`}
      id={`draw-tool-${item[1]}-geometry-button`}
      value={`${item[0]}`}
      title={`${item[0]}`}
      onClick={handleGeometryChange}
    ></GeometryButton>

    )
  })

  let style = { display: "initial" };
  if (visible) {
    style.display = "initial";
  } else {
    style.display = "none";
  }

  return (
    <div ref={drawtoolWidgetDiv} style={style}>
      <div id="infoDiv" className="esri-widget">
        <Typography variant='subtitle1' >Select Geometry to Begin Drawing:</Typography>
        <Stack direction="row" spacing={1} sx={{flexWrap:"wrap"}}>
        {GeometryButtons}
        <IconButton
              title="clear selected"
              onClick={(e) => {
                sketchViewModel.delete();
              }}
            >
              <DeleteIcon />
            </IconButton>
        <IconButton
        title="clear all drawings"
          id="draw-tool-clearDrawing"
          onClick={() => clearDrawing(view)}
        >
          <DeleteSweepIcon />
        </IconButton>
        </Stack>
        {activeGeometry==='point'?
        <>
        <FormControl fullWidth>
        <InputLabel id="select-draw-point-style">Point Style</InputLabel>
        <Select 
        variant="standard"
        labelId="select-draw-point-style" onChange={handlePointStyleChange} value={pointStyle}>
        {pointStyleOptions}
        </Select>
        </FormControl>
        <br />
        <Typography>Point Symbol Size </Typography>
        <Slider
          size='medium'
          title="Point Symbol Size"
          id="Size"
          value={parseInt(symbolSize)}
          name="symbolSize"
          min={0.5}
          max={100}
          step={0.5}
          onChange={handleSymbolSize}
          valueLabelDisplay="auto"
        />
                <br />
                <Typography>Point Symbol Angle </Typography>
                <Slider
          size='medium'
          title="Point Symbol Angle"
          id="Angle"
          value={symbolAngle}
          name="symbolAngle"
          min={0}
          max={360}
          step={1}
          onChange={handleSymbolAngle}
          valueLabelDisplay="auto"
        />
        </>
        :""}
        {activeGeometry==='text'?
        <>
        <br/>
        <br/>
        <TextField
        variant="standard"
        label={`Text Label`}
          fullWidth
          placeholder="Your Text"
          value={textLabel}
          onChange={handleTextLabel}
        />
        <br/>
        <br/>
        <FormControl fullWidth>
        <InputLabel id="font-family-draw-option">Font family</InputLabel>
        <Select
        variant="standard"
        labelId="font-family-draw-option"
         onChange={handleFontFamilyChange} value={fontfamily}>
        {fontFamilyOptions}
        </Select>
        </FormControl>
        <Typography>Text Size </Typography>
        <Slider
        size="medium"
          title="Text Size"
          id="textSize"
          value={parseInt(fontSize)}
          name="textSize"
          min={0.5}
          max={100}
          step={0.5}
          onChange={handleFontSize}
          valueLabelDisplay="auto"
        />
        <br />
        <Typography>Text Symbol Angle </Typography>
          <Slider
          size='medium'
          title="Text Symbol Angle"
          id="text-angle"
          value={symbolAngle}
          name="textAngle"
          min={0}
          max={360}
          step={1}
          onChange={handleSymbolAngle}
          valueLabelDisplay="auto"
        />
        </>
        :""}
        {activeGeometry.length && activeGeometry!=='polyline'?
        <>
        <br/>
        <br/>
        <Typography>Fill Color </Typography>
        <Input
        fullWidth
          type="color"
          value={fillColor}
          onChange={handleFillColorChange}
          endAdornment={
            <div  style={{
              width: '48px',
              height: '48px',
            }}></div>

          }
        />
        <br/>
        <Typography>Fill Opacity</Typography>
        <Slider
          title="Opacity"
          id="fillOpacity"
          name="fillOutlineopacity"
          value={parseFloat(fillOpacity)}
          min={0}
          max={1}
          step={0.1}
          onChange={handleFillOpacityChange}
          valueLabelDisplay="auto"
        />
        </>
        :""}
        {activeGeometry==='polygon'||activeGeometry==='circle'||activeGeometry==='rectangle'?
        <>
        <br/>
        <FormControl fullWidth >
        <InputLabel id={`fill-style-draw-input`}>Fill Style </InputLabel>
        <Select 
        variant="standard"
        labelId={`fill-style-draw-input`}
        onChange={handleFillStyleChange} value={fillStyle}>{fillStyleOptions}
        </Select>
        </FormControl>
        </>:""}
        {activeGeometry.length>0?
        <>
        <br></br>
        <Typography>Outline Color</Typography>
        <Input
        fullWidth
          type="color"
          value={outlineColor}
          onChange={handleOutlineColorChange}
          endAdornment={
            <div  style={{
              width: '48px',
              height: '48px',
            }}></div>

          }
        />{" "}
        <br/>
        <Typography>Outline Opacity</Typography>
        <Slider
          title="Opacity"
          id="outlineopacity"
          name="outlineopacity"
          value={parseFloat(outlineOpacity)}
          min={0}
          max={1}
          step={0.1}
          onChange={handleOutlineOpacityChange}
          valueLabelDisplay="auto"
        />
        </>:""}
        {activeGeometry==='polygon'||activeGeometry==='circle'||activeGeometry==='rectangle'||activeGeometry==='polyline'?
        <>
        <br></br>
        <FormControl fullWidth>
          <InputLabel
          id="line-style-draw-input"
          >
            Line style
          </InputLabel>
        <Select
        variant="standard"
        labelId="line-style-draw-input"
        onChange={handleLineStyleChange} value={lineStyle}>
        {lineStyleOptions}
        </Select>
        </FormControl>
        </>:""}
        <br/>
        <br/>
          {sketchViewModel && <SnappingControlsWidget view={view} activeWidget={"snappingOptions"} sketchViewModel={sketchViewModel} handleName="draw"/>}
      </div>
    </div>
  );
};
export default DrawToolWidget;